import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { IconProps } from "lib/icons/untitled_ui/UIcon";
import Button from "../button/Button";

type CTACardProps = {
  title: string;
  description?: React.ReactNode;
  imageSrc?: string;
  altText?: string;
  onClick?: () => void;
  horizontal?: boolean;
  imageStyles?: React.CSSProperties;
  illustrationContainerHeight?: string | number;
  noIllustration?: boolean;
  height?: number;
  customContent?: React.ReactNode;
  buttonProps?: {
    buttonText?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
    isLoading?: boolean;
    ariaLabel?: string;
    width?: number | string;
    icon?: IconProps["icon"];
  };
};

export default function CTACard({
  title,
  description,
  imageSrc,
  altText,
  onClick,
  horizontal = false,
  illustrationContainerHeight,
  noIllustration = false,
  imageStyles,
  height,
  customContent,
  buttonProps,
}: CTACardProps) {
  return (
    <Container horizontal={horizontal} height={height} onClick={onClick}>
      <IllustrationContainer
        horizontal={horizontal}
        height={illustrationContainerHeight}
        hasCustomContent={!!customContent}
        noIllustration={noIllustration}
      >
        {customContent
          ? customContent
          : imageSrc && <img src={imageSrc} alt={altText} style={imageStyles} />}
      </IllustrationContainer>

      <ContentContainer horizontal={horizontal}>
        <TextContainer>
          <Text body bold>
            {title}
          </Text>
          {description &&
            (typeof description === "string" ? (
              <Text description color="sysTextSecondary">
                {description}
              </Text>
            ) : (
              description
            ))}
        </TextContainer>
        {buttonProps?.buttonText && (
          <Button
            buttonType="secondary"
            icon={buttonProps.icon}
            onClick={(event) => {
              event.stopPropagation();
              buttonProps?.onClick?.(event);
            }}
            disabled={buttonProps.disabled}
            loading={buttonProps.isLoading}
            aria-label={buttonProps.ariaLabel}
            width={buttonProps.width ?? (horizontal ? undefined : 144)}
          >
            {buttonProps.buttonText}
          </Button>
        )}
      </ContentContainer>
    </Container>
  );
}

const CARD_HEIGHT = 300;

const Container = styled(Flex)<{ horizontal: boolean; height?: number }>(
  ({ theme, horizontal, height, onClick }) => ({
    flex: 1,
    flexDirection: horizontal ? "row" : "column",
    overflow: "hidden",
    width: "100%",
    minWidth: 200,
    height: height ?? (horizontal ? CARD_HEIGHT : "auto"),
    minHeight: height ?? (horizontal ? CARD_HEIGHT : "auto"),

    borderRadius: 8,
    border: `1px solid ${theme.colors.sysBorderDecorative}`,
    backgroundColor: theme.colors.white,
    cursor: onClick ? "pointer" : "default",
  }),
);

const IllustrationContainer = styled(Flex)<{
  horizontal: boolean;
  height?: string | number;
  hasCustomContent?: boolean;
  noIllustration: boolean;
}>(({ theme, horizontal, height = 189, hasCustomContent, noIllustration }) => ({
  display: noIllustration ? "none" : "flex",
  flexShrink: 0,
  flexGrow: hasCustomContent && horizontal ? 1 : 0,
  justifyContent: hasCustomContent ? "flex-start" : "center",
  alignItems: hasCustomContent ? "stretch" : "center",
  order: horizontal ? 2 : 1,
  overflow: "hidden",
  width: hasCustomContent && horizontal ? "auto" : horizontal ? "50%" : "100%",
  height: horizontal ? "100%" : height,
  padding: hasCustomContent ? "24px" : horizontal ? "32px 32px 0 32px" : "24px 24px 0 24px",
  background: hasCustomContent
    ? "transparent"
    : "linear-gradient(180deg, #FFF -47.34%, #F7F9FA 100%)",
  borderBottom: hasCustomContent
    ? "none"
    : horizontal
      ? "none"
      : `1px solid ${theme.colors.sysBorderDecorative}`,
  borderLeft: hasCustomContent
    ? "none"
    : horizontal
      ? `1px solid ${theme.colors.sysBorderDecorative}`
      : "none",
}));

const ContentContainer = styled(Flex)<{ horizontal: boolean }>(({ horizontal }) => ({
  flexDirection: "column",
  flex: 1,
  justifyContent: "center",
  alignItems: "flex-start",
  order: horizontal ? 1 : 2,
  width: horizontal ? "50%" : "100%",
  padding: 24,
  minHeight: 0,
  gap: 16,
}));

const TextContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});
